import React, { useMemo, useCallback } from 'react';
import cx from 'classnames';
import { withParentSize } from '@visx/responsive';
import { AxisRight, AxisLeft } from '@visx/axis';
import { Grid } from '@visx/grid';
import LineWithRings from 'js/components/Charts/Elements/LineWithRings';
import { titleCase } from 'js/utils/string';

import { scaleLinear } from '@visx/scale';
import { useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import SprintTooltip, {
  formatAmount,
  formatPercent,
  formatResized,
  sprintTooltipStyles,
} from '../SprintTooltip';
import Sprint, { deliveredBreakdown } from './Sprint';

import styles from './Velocity.module.scss';
import { uniqueId } from 'lodash';

const tickLabelPropsFactory = (scale) => (d) => {
  const result = {
    fontSize: 9,
    strokeWidth: 0,
    paintOrder: 'stroke',
    textAnchor: 'start',
    y: scale(d) - 5,
    x: 4,
    className: 'tick-label',
  };

  return result;
};

const tickLabelPropsFactorySayDo = (scale) => (d) => {
  const result = {
    fontSize: 9,
    strokeWidth: 0,
    paintOrder: 'stroke',
    textAnchor: 'end',
    y: scale(d) - 5,
    x: -4,
    className: 'tick-label',
  };

  return result;
};

function sayDo(committed, completed, units) {
  const committedValue = committed[units] ?? 0;
  const completedValue = completed[units] ?? 0;

  return committedValue > 0 ? completedValue / committedValue : null;
}

function calculateVelocitySayDo({ committed, completed_total }, units) {
  return sayDo(committed, completed_total, units);
}

// Committed work measured at its start-of-sprint sizes on both sides, so resizing
// mid-sprint doesn't move the ratio
function calculateTrueSayDo(
  { committed, completed_of_committed_at_start },
  units
) {
  return sayDo(committed, completed_of_committed_at_start, units);
}

function mapSeries(series, yVal, xOffset = 0) {
  return series
    .map((d, i) => ({ x: i + xOffset, y: yVal(d) }))
    .filter((d) => d.y != null);
}

function tooltipSections(sprint, units) {
  const { metrics } = sprint;
  const { planned, support, bonus } = deliveredBreakdown(sprint, units);
  const amount = (value) => formatAmount(value, units);

  const sections = [
    {
      title: 'Target',
      rows: [
        {
          label: 'Targeted',
          value: formatResized(
            metrics.committed[units],
            metrics.committed_at_end[units],
            units
          ),
          marker: styles.committed,
        },
        {
          label: 'Completed',
          value: formatResized(
            metrics.completed_of_committed_at_start[units],
            metrics.completed_of_committed[units],
            units
          ),
          marker: styles['completed-of-committed'],
        },
      ],
    },
    {
      title: 'Delivered',
      rows: [
        { label: 'Planned', value: amount(planned), marker: styles.planned },
        { label: 'Unplanned', value: amount(support), marker: styles.support },
        ...(bonus > 0
          ? [{ label: 'Bonus', value: amount(bonus), marker: styles.bonus }]
          : []),
        {
          label: 'Total',
          value: amount(planned + support + bonus),
          total: true,
        },
      ],
    },
  ];

  // Say/do is only charted once a sprint is complete
  if (sprint.sprint.complete_date !== null) {
    sections.push({
      title: 'Say/Do',
      rows: [
        {
          label: 'True',
          value: formatPercent(calculateTrueSayDo(metrics, units)),
        },
        {
          label: 'Relative',
          value: formatPercent(calculateVelocitySayDo(metrics, units)),
        },
      ],
    });
  }

  return sections;
}

function formatUnits(units) {
  if (units === 'story_points') {
    return 'Story Points';
  } else if (units === 'time') {
    return 'Time';
  } else if (units === 'tasks') {
    return 'Tasks';
  } else {
    return titleCase(units);
  }
}

function VelocityChart({
  parentWidth: width,
  height = 300,
  margins = { top: 5, right: 20, bottom: 20, left: 20 },
  data,
  sprintCount = 7,
  sprintPaddingPercentage = 0.6,
  units = 'story_points',
}) {
  margins = { top: 20, right: 20, bottom: 20, left: 20, ...margins };
  const chartWidth = width - margins.left - margins.right;
  const chartHeight = height - margins.top - margins.bottom;

  // get the last 7 sprints
  const sprints = useMemo(() => {
    const x = data.slice(-sprintCount).reverse();
    // return [...x, ...x, ...x, ...x].slice(-sprintCount)
    return x;
  }, [data, sprintCount]);

  const {
    sprintWidth,
    paddingWidth,
    committedWidth,
    deliveredWidth,
    gapWidth,
  } = useMemo(() => {
    const sprintWidth =
      chartWidth /
      (sprintCount * (1 + sprintPaddingPercentage) + sprintPaddingPercentage);
    const paddingWidth = sprintWidth * sprintPaddingPercentage;
    const committedWidth = sprintWidth * 0.25;
    const deliveredWidth = sprintWidth * 0.6;
    const gapWidth = sprintWidth - committedWidth - deliveredWidth;
    return {
      sprintWidth,
      paddingWidth,
      committedWidth,
      deliveredWidth,
      gapWidth,
    };
  }, [chartWidth, sprintCount, sprintPaddingPercentage]);

  const { velocitySayDo, trueSayDo } = useMemo(() => {
    const sprintData = data.slice(-(sprintCount + 1)).reverse();

    const calculatedValues = sprintData.reduce((acc, sprint) => {
      acc.push(
        sprint.sprint.complete_date !== null
          ? {
              velocitySayDo: calculateVelocitySayDo(sprint.metrics, units),
              trueSayDo: calculateTrueSayDo(sprint.metrics, units),
            }
          : null
      );

      return acc;
    }, []);

    return {
      velocitySayDo: mapSeries(calculatedValues, (d) => d?.velocitySayDo) ?? [],
      trueSayDo: mapSeries(calculatedValues, (d) => d?.trueSayDo) ?? [],
    };
  }, [data, units, sprintCount]);

  const yScaleSayDo = useMemo(
    () =>
      scaleLinear({
        domain: [
          0,
          Math.max(
            1.2,
            1.2 *
              Math.max(
                ...velocitySayDo
                  .filter((d) => d.x < sprintCount)
                  .map((d) => d.y),
                ...trueSayDo.filter((d) => d.x < sprintCount).map((d) => d.y)
              )
          ),
        ],
        range: [chartHeight, 0],
      }),
    [velocitySayDo, trueSayDo, chartHeight, sprintCount]
  );

  const xScale = scaleLinear({
    domain: [0, sprintCount - 1],
    range: [chartWidth - paddingWidth - sprintWidth, paddingWidth],
  });

  const xSayDo = useCallback(
    (d) => {
      return xScale(d.x);
    },
    [xScale]
  );

  const ySayDo = useCallback(
    (d) => {
      return yScaleSayDo(d.y);
    },
    [yScaleSayDo]
  );

  const yScale = scaleLinear({
    domain: [
      0,
      1.1 *
        Math.max(
          ...data.flatMap((sprint) => [
            sprint.metrics.completed_total[units],
            sprint.metrics.committed[units],
          ])
        ) || 1,
    ],
    range: [chartHeight, 0],
  });

  const formatSizeTick = useCallback(
    (size) => {
      if (yScale(size) < 15) {
        return '';
      }
      return size;
    },
    [yScale]
  );

  const formatSayDoTick = useCallback(
    (d) => {
      if (yScaleSayDo(d) < 15) {
        return '';
      }

      return (d * 100).toFixed(0) + '%';
    },
    [yScaleSayDo]
  );

  const maskId = useMemo(() => uniqueId('velocity-say-do-mask-'), []);

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const handleSprintHover = useCallback(
    (event, sprint) => {
      const coords = localPoint(event);
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: sprint,
      });
    },
    [showTooltip]
  );

  return (
    <div className={sprintTooltipStyles.chart}>
      <svg viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <mask id={maskId}>
            <rect
              x={-(committedWidth + gapWidth / 2)}
              y={0}
              width={chartWidth}
              height={height}
              fill="white"
            />
          </mask>
        </defs>

        <g transform={`translate(${margins.left}, ${margins.top})`}>
          <g>
            <rect
              x={0}
              y={0}
              width={chartWidth}
              height={yScaleSayDo(0.8) - yScaleSayDo(1.2)}
              className={styles['say-do-target-zone']}
            />
          </g>
          <g>
            <Grid
              className="chart-grid chart-neutral-line"
              height={chartHeight}
              width={chartWidth}
              rowTickValues={yScale.ticks(4).filter((tick) => tick > 0)}
              numTicksRows={5}
              numTicksColumns={0}
              xScale={xScale}
              yScale={yScale}
            />
            <AxisRight
              scale={yScale}
              axisClassName="chart-axis"
              tickFormat={formatSizeTick}
              tickLineProps={{ className: 'tick' }}
              tickLabelProps={tickLabelPropsFactory(yScale)}
              numTicks={5}
              tickValues={yScale.ticks(4).filter((tick) => tick > 0)}
            />
            <g transform={`translate(-5, ${chartHeight / 2})`}>
              <text
                textAnchor="middle"
                transform={'rotate(270)'}
                className="chart-axis-label chart-light"
              >
                {formatUnits(units)}
              </text>
            </g>
            <g transform={`translate(${chartWidth}, 0)`}>
              <AxisLeft
                scale={yScaleSayDo}
                axisClassName="chart-axis"
                tickFormat={formatSayDoTick}
                tickLineProps={{ className: 'tick' }}
                tickLabelProps={tickLabelPropsFactorySayDo(yScaleSayDo)}
                numTicks={5}
                tickValues={yScaleSayDo.ticks(4).filter((tick) => tick > 0)}
              />
              <g transform={`translate(11, ${chartHeight / 2})`}>
                <text
                  textAnchor="middle"
                  transform={'rotate(270)'}
                  className="chart-axis-label chart-light"
                >
                  Say/Do Ratio
                </text>
              </g>
            </g>
          </g>
          <g>
            {sprints.map((sprint, i) => (
              <g
                key={i + 'sprint-' + sprint.sprint.id}
                transform={`translate(${xScale(i)}, 0)`}
              >
                <Sprint
                  sprint={sprint}
                  committedWidth={committedWidth}
                  deliveredWidth={deliveredWidth}
                  gapWidth={gapWidth}
                  yScale={yScale}
                  chartHeight={chartHeight}
                  units={units}
                />
              </g>
            ))}
          </g>

          <g
            transform={`translate(${committedWidth + gapWidth / 2}, 0)`}
            mask={`url(#${maskId})`}
          >
            <g className={styles['velocity-say-do-line']}>
              <LineWithRings data={velocitySayDo} x={xSayDo} y={ySayDo} />
            </g>
            <g className={styles['true-say-do-line']}>
              <LineWithRings data={trueSayDo} x={xSayDo} y={ySayDo} />
            </g>
          </g>
          <rect
            x="0"
            y="0"
            width={chartWidth}
            height={chartHeight}
            className="chart-box"
          />
          <g>
            {sprints.map((sprint, i) => (
              <rect
                key={i + 'hover-' + sprint.sprint.id}
                x={xScale(i) - paddingWidth / 2}
                y={0}
                width={sprintWidth + paddingWidth}
                height={chartHeight}
                className={cx(sprintTooltipStyles['hover-column'], {
                  [sprintTooltipStyles.hovered]: tooltipData === sprint,
                })}
                onMouseMove={(event) => handleSprintHover(event, sprint)}
                onMouseLeave={hideTooltip}
              />
            ))}
          </g>
        </g>
      </svg>
      {tooltipOpen && tooltipData && (
        <SprintTooltip
          left={tooltipLeft}
          top={tooltipTop}
          sprint={tooltipData.sprint}
          units={units}
          sections={tooltipSections(tooltipData, units)}
        />
      )}
      <div>
        <div
          className={styles['sprint-names']}
          style={{
            gap: `${paddingWidth}px`,
            margin: `-1rem ${paddingWidth + 15}px 0`,
            '--sprint-count': sprintCount,
          }}
        >
          {sprints.map((s) => (
            <div key={s.sprint.id}>
              {s.sprint.name}
              {s.sprint.complete_date === null && <div>(active)</div>}
            </div>
          ))}
        </div>
      </div>
      <div className="legend-bottom">
        <div className="legend">
          <div className={cx('legend-set', styles['issue-state-legend'])}>
            <div className={'legend-label'}>Target:</div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.committed)}></span>
              Targeted
            </div>
            <div className={'legend-item'}>
              <span
                className={cx(
                  'legend-marker',
                  styles['completed-of-committed']
                )}
              ></span>
              Completed
            </div>
          </div>
          <div className={cx('legend-set', styles['sprint-start-end-legend'])}>
            <div className={'legend-label'}>Delivered:</div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.planned)}></span>
              Planned
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.support)}></span>
              Unplanned
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.bonus)}></span>
              Bonus
            </div>
          </div>
          <div className={cx('legend-set', styles['issue-delta-legend'])}>
            <div className={'legend-label'}>Say/Do:</div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.true)}></span>
              True
            </div>
            <div className={'legend-item'}>
              <span className={cx('legend-marker', styles.relative)}></span>
              Relative
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withParentSize(VelocityChart);
